/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { CLUModelGuidanceSwagger } from 'src/clu/core/services/trainedModels/cluModelGuidanceSwaggers';
import { VerificationSwagger } from 'src/clu/core/services/trainedModels/cluModelVerificationSwagger';
import { CluTrainedModelEvaluationSwagger } from 'src/clu/core/services/trainedModels/cluTrainedModelSwaggers';
import { createOperationCall, createOperationCallWithJobSwaggers, OperationHandler } from 'src/core/services/api/createOperationCall';
import { ResourceListResults } from 'src/core/types/ResourceListTypes';
import { httpClient } from 'src/core/utils/api/httpClient';
import { luisRequestOptionsBuilder } from 'src/core/utils/api/luisRequestOptionsBuilder';
import { urlBuilder } from 'src/core/utils/api/urlBuilder';
import { createService } from 'src/core/utils/createService';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { getPortalFromUrl } from 'src/core/utils/routingUtils';
import { LoadSnapshotJobStatusSwagger } from 'src/modules/modelDetails/types/loadSnapshotJobStatusSwagger';
import { EvaluationOnDemandJobInputSwagger, EvaluationOnDemandJobSwagger } from 'src/modules/modelEvaluation/modelEvaluationSwaggers';
import { TrainedModelSwagger } from 'src/modules/modelTraining/types/trainingSwaggers';

export const getCluTrainedModelService = createService(({ context, getEnvironment }) => {
    const { webApiUrl, apiUrl, isProduction } = getEnvironment({
        apiName: 'clu'
    });

    const { serviceName } = getPortalFromUrl();

    const apiVersion =
        serviceName === 'summarization' ? LuisConstants.Summarization.CoversationAuthoringVersion : LuisConstants.Clu.AuthoringApiVersion;

    const apiOptions = luisRequestOptionsBuilder()
        .useDefaultOptions(context.serviceKey, isProduction)
        .build();

    const getUrl = (projectName: string, trainingModelName: string, resource?: string) => {
        const builder = urlBuilder()
            .baseUrl(apiUrl)
            .project(projectName)
            .resource('models', trainingModelName)
            .queryParam('api-version', apiVersion);

        if (resource) {
            builder.resource(resource);
        }

        return builder.build();
    };

    const getRefreshEvaluationUrl = (projectName: string, trainedModelName: string) =>
        urlBuilder()
            .baseUrl(apiUrl)
            .project(projectName)
            .model(trainedModelName)
            .resource(':evaluate')
            .queryParam('api-version', LuisConstants.Clu.AuthoringApiVersion)
            .build();

    const getModelGuidanceUrl = (projectName: string, trainedModelName: string) =>
        urlBuilder()
            .baseUrl(webApiUrl)
            .resource('internal')
            .project(projectName)
            .model(trainedModelName)
            .resource('modelguidance')
            .queryParam('api-version', LuisConstants.Clu.InternalApiVersion)
            .build();

    const getVerificationUrl = (projectName: string, trainedModelName: string) =>
        urlBuilder()
            .baseUrl(apiUrl)
            .project(projectName)
            .model(trainedModelName)
            .resource('evaluation', 'result')
            .queryParam('api-version', apiVersion)
            .queryParam('maxpagesize', '1000000')
            .queryParam('stringIndexType', 'Utf16CodeUnit')
            .build();

    const getCluTrainedModels = async ({
        projectName,
        nextLink
    }: {
        projectName: string;
        nextLink?: string;
    }): Promise<ResourceListResults<TrainedModelSwagger>> => {
        const url =
            nextLink ||
            urlBuilder()
                .baseUrl(apiUrl)
                .project(projectName)
                .resource('models')
                .queryParam('api-version', apiVersion)
                .build();

        const { data: trainedModels } = await httpClient.get<ResourceListResults<TrainedModelSwagger>>(url, apiOptions);

        return trainedModels;
    };

    const getCluTrainedModel = async ({
        projectName,
        trainedModelName
    }: {
        projectName: string;
        trainedModelName: string;
    }): Promise<TrainedModelSwagger> => {
        const url = urlBuilder()
            .baseUrl(apiUrl)
            .project(projectName)
            .model(trainedModelName)
            .queryParam('api-version', apiVersion)
            .build();

        const { data: trainedModel } = await httpClient.get<TrainedModelSwagger>(url, apiOptions);

        return trainedModel;
    };

    const getVerificationData = async ({
        nextLink,
        projectName,
        trainedModelName
    }: {
        nextLink?: string;
        projectName: string;
        trainedModelName: string;
    }): Promise<ResourceListResults<VerificationSwagger>> => {
        const url = nextLink ? `${nextLink}&stringIndexType=Utf16CodeUnit` : getVerificationUrl(projectName, trainedModelName);

        const { data: verificationData } = await httpClient.get<ResourceListResults<VerificationSwagger>>(url, apiOptions);

        return verificationData;
    };

    const getTrainedModelGuidanceReport = async ({
        projectName,
        trainedModelName
    }: {
        projectName: string;
        trainedModelName: string;
    }): Promise<CLUModelGuidanceSwagger> => {
        const url = getModelGuidanceUrl(projectName, trainedModelName);

        const { data: modelGuidanceReport } = await httpClient.get<CLUModelGuidanceSwagger>(url, apiOptions);
        return modelGuidanceReport;
    };

    const getCluTrainedModelEvaluationData = async ({
        projectName,
        trainedModelName
    }: {
        projectName: string;
        trainedModelName: string;
    }): Promise<CluTrainedModelEvaluationSwagger> => {
        const url = getUrl(projectName, trainedModelName, 'evaluation/summary-result');
        const { data: evaluationData } = await httpClient.get<CluTrainedModelEvaluationSwagger>(url, apiOptions);

        return evaluationData;
    };

    const deleteTrainedModel = async ({
        projectName,
        trainedModelName
    }: {
        projectName: string;
        trainedModelName: string;
    }): Promise<void> => {
        await httpClient.delete<void>(getUrl(projectName, trainedModelName), apiOptions);
    };

    const loadModelSnapshotAndPoll = async ({
        projectName,
        trainedModelName,
        operationHandler
    }: {
        projectName: string;
        trainedModelName: string;
        operationHandler: OperationHandler<LoadSnapshotJobStatusSwagger>;
    }): Promise<void> => {
        const { headers } = await httpClient.post<void>(getUrl(projectName, trainedModelName, ':load-snapshot'), null, apiOptions);

        const statusUrl = (<Record<string, string>>headers)[LuisConstants.OperationHeaders.CTLocationHeader];

        await createOperationCall(statusUrl, apiOptions, operationHandler);
    };

    const initiateRefreshEvaluationAndPoll = async ({
        projectName,
        trainedModelName
    }: {
        projectName: string;
        trainedModelName: string;
    }): Promise<string> => {
        const modifiedApiOptions = luisRequestOptionsBuilder().useDefaultOptions(context.serviceKey, isProduction);

        if (!isProduction) {
            modifiedApiOptions.useRemoveHeader('Azure-Resource-Id');
        }

        const payload: EvaluationOnDemandJobInputSwagger = {
            kind: 'manual'
        };

        const { headers } = await httpClient.post<void>(
            getRefreshEvaluationUrl(projectName, trainedModelName),
            payload,
            modifiedApiOptions.build()
        );

        const statusUrl = (<Record<string, string>>headers)[LuisConstants.OperationHeaders.CTLocationHeader];
        return statusUrl;
    };

    const pollRefreshEvaluation = async ({
        statusUrl,
        operationHandler
    }: {
        statusUrl?: string;
        operationHandler: OperationHandler<EvaluationOnDemandJobSwagger>;
    }): Promise<void> => {
        await createOperationCallWithJobSwaggers(statusUrl, apiOptions, operationHandler);
    };

    return {
        getCluTrainedModel,
        deleteTrainedModel,
        getCluTrainedModels,
        getVerificationData,
        loadModelSnapshotAndPoll,
        getTrainedModelGuidanceReport,
        getCluTrainedModelEvaluationData,
        initiateRefreshEvaluationAndPoll,
        pollRefreshEvaluation
    };
});
